import { observer } from 'mobx-react-lite';
import { goToNextDayText } from 'owa-locstrings/lib/strings/gotonextdaytext.locstring.json';
import { goToPreviousDayText } from 'owa-locstrings/lib/strings/gotopreviousdaytext.locstring.json';
import loc, { format } from 'owa-localize';
import { IconButton } from '@fluentui/react/lib/Button';
import { DateRangeType } from '@fluentui/date-time-utilities/lib/dateValues/dateValues';
import {
    ComposeSchedulingGrid,
    navigateToDate,
    getScenarioStore,
} from 'owa-calendar-scheduling-grid';
import { ControlIcons } from 'owa-control-icons';
import { addDays, formatMonthDayYear, OwaDate } from 'owa-datetime';
import type { DateRange } from 'owa-datetime-utils';
import { LiveNotification } from 'owa-live-notification';
import { logChangeTimeActions } from 'owa-calendar-compose-form-actions/lib/actions/eventMetricsActions';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import {
    SchedulingFreeBusyItemTemplate,
    SchedulingSelectionItemTemplate,
} from 'owa-calendar-forms-common/lib/components/SchedulingTemplates';
import type SchedulingCalendarEvent from 'owa-calendar-forms-common/lib/schema/SchedulingCalendarEvent';
import { Timestrip } from 'owa-timestrip';
import dateRangeToDisplayString from 'owa-timestrip/lib/utils/dateRangeToDisplayString';
import * as React from 'react';
import {
    updateDateTimes,
    updateAllDay,
    logSchedulingGridUsed,
} from 'owa-calendar-compose-form-actions';
import FreeBusySummary from './FreeBusySummary';
import { TimeChangeActionType } from 'owa-calendar-helpers-types/lib/TimeChangeActionType';
import { isFeatureEnabled } from 'owa-feature-flags';
import { lazyGetSchedulingGridBookends } from 'owa-mobility-compose';
import {
    getComposeEvent,
    getOriginalEvent,
    getFullComposeViewState,
} from 'owa-calendar-compose-store-manager';

const styles = require('./FullComposeRightPane.scss');

export interface FullComposeRightPaneProps {
    className: string;
    formId: string | null;
}

export default observer(function FullComposeRightPane(props: FullComposeRightPaneProps) {
    const { className, formId } = props;
    const event = getComposeEvent(formId);
    const originalEvent = getOriginalEvent(formId);
    const fullComposeViewState = getFullComposeViewState(formId);

    let userOptions = getUserConfiguration().UserOptions;
    const gridViewState = getScenarioStore(fullComposeViewState?.schedulingGridScenarioId);
    let selectedDate = gridViewState?.selectedDate;
    const getSchedulingGridBookends = isFeatureEnabled('cal-mf-mobility')
        ? lazyGetSchedulingGridBookends.tryImportForRender()
        : () => null;
    const gridBookends = getSchedulingGridBookends ? getSchedulingGridBookends() : null;

    const schedulingFreeBusyItemTemplate = React.useCallback(
        (store: {}, model: SchedulingCalendarEvent) => {
            return <SchedulingFreeBusyItemTemplate model={model} event={event} />;
        },
        [event]
    );

    const selectedItemTemplate = React.useCallback(
        (store: {}, model: SchedulingCalendarEvent) => {
            return (
                <SchedulingSelectionItemTemplate
                    model={model}
                    event={event}
                    originalEvent={originalEvent}
                    bookends={gridBookends}
                />
            );
        },
        [event, originalEvent, gridBookends]
    );

    const onSchedulingGridClick = React.useCallback(
        (start: OwaDate, end: OwaDate, isAllDay: boolean) => {
            let { IsAllDayEvent } = getComposeEvent(formId);
            if (IsAllDayEvent != isAllDay) {
                updateAllDay(isAllDay);
            } else if (IsAllDayEvent) {
                return;
            }
            updateDateTimes({ start: start, end: end }, TimeChangeActionType.SchedulingGrid);
            logSchedulingGridUsed();
        },
        [formId]
    );

    const onDateRangeChange = React.useCallback(
        (dateRange: DateRange) => {
            navigateToDate(fullComposeViewState?.schedulingGridScenarioId, dateRange.start);
            logChangeTimeActions(TimeChangeActionType.SchedulingGridBrowseDate);
        },
        [fullComposeViewState]
    );

    const onSchedulingGridClickForward = React.useCallback(() => {
        const gridViewState = getScenarioStore(fullComposeViewState?.schedulingGridScenarioId);
        if (!gridViewState) {
            // not initialized yet
            return;
        }
        const schedulingGridDate = gridViewState.selectedDate;
        navigateToDate(
            fullComposeViewState?.schedulingGridScenarioId,
            addDays(schedulingGridDate, 1)
        );
        logChangeTimeActions(TimeChangeActionType.SchedulingGridBrowseForward);
    }, [fullComposeViewState]);

    const onSchedulingGridClickBackward = React.useCallback(() => {
        const gridViewState = getScenarioStore(fullComposeViewState?.schedulingGridScenarioId);
        if (!gridViewState) {
            // not initialized yet
            return;
        }
        const schedulingGridDate = gridViewState.selectedDate;
        navigateToDate(
            fullComposeViewState?.schedulingGridScenarioId,
            addDays(schedulingGridDate, -1)
        );
        logChangeTimeActions(TimeChangeActionType.SchedulingGridBrowseBackward);
    }, [fullComposeViewState]);

    if (!event || !selectedDate) {
        return <span className={className} />;
    }
    const previousLabel = format(
        loc(goToPreviousDayText),
        '\n' + formatMonthDayYear(addDays(selectedDate, -1))
    );
    const nextLabel = format(
        loc(goToNextDayText),
        '\n' + formatMonthDayYear(addDays(selectedDate, 1))
    );

    return (
        <span className={className}>
            <div className={styles.schedulingColumn}>
                <div className={styles.navigation}>
                    <IconButton
                        className={styles.navigationIcon}
                        iconProps={{ iconName: ControlIcons.Back }}
                        title={previousLabel}
                        ariaLabel={previousLabel}
                        onClick={onSchedulingGridClickBackward}
                    />
                    <IconButton
                        className={styles.navigationIcon}
                        iconProps={{ iconName: ControlIcons.Forward }}
                        title={nextLabel}
                        ariaLabel={nextLabel}
                        onClick={onSchedulingGridClickForward}
                    />
                    <Timestrip
                        className={styles.timestrip}
                        dateRange={{
                            start: selectedDate,
                            end: selectedDate,
                        }}
                        dateRangeType={DateRangeType.Day}
                        weekStartDay={userOptions.WeekStartDay}
                        showWeekNumbers={userOptions.ShowWeekNumbers}
                        firstWeekOfYear={userOptions.FirstWeekOfYear}
                        workDays={[]}
                        useWeekDay={true}
                        onDateRangeChange={onDateRangeChange}
                        ariaRole="button"
                    />
                    <LiveNotification
                        message={dateRangeToDisplayString(
                            {
                                start: selectedDate,
                                end: selectedDate,
                            },
                            DateRangeType.Day
                        )}
                        atomic={true}
                    />
                </div>
                <div className={styles.schedulingGrid}>
                    <ComposeSchedulingGrid
                        scenarioId={fullComposeViewState?.schedulingGridScenarioId}
                        conflictTemplate={schedulingFreeBusyItemTemplate}
                        selectionTemplate={selectedItemTemplate}
                        onDateTimesChanged={onSchedulingGridClick}
                        editMode={true}
                        shouldAdjustStartTimeEndTime={
                            fullComposeViewState.shouldAdjustStartTimeEndTime
                        }
                        showFreeBusyData={true}
                    />
                </div>
                <div className={styles.freeBusy}>
                    <FreeBusySummary maxConflicts={6} formId={formId} />
                </div>
            </div>
        </span>
    );
});
